import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useVolunteerAuth } from '../contexts/VolunteerAuthContext';
import OrgProgrammesVolunteer from '../components/volunteer/programmes/OrgProgrammesVolunteer';
import JobBoardVolunteer from '../components/volunteer/programmes/JobBoardVolunteer';
import SupportNetworkVolunteer from '../components/volunteer/programmes/SupportNetworkVolunteer';
import FinancialAssistanceVolunteer from '../components/volunteer/programmes/FinancialAssistanceVolunteer';
import MyEventsVolunteer       from '../components/volunteer/events/MyEventsVolunteer';
import VolunteerHome           from '../components/volunteer/home/VolunteerHome';
import NotificationBell        from '../components/shared/NotificationBell';
import {
  getVolunteer,
  updateVolunteer,
  VOLUNTEER_SKILLS,
  LANGUAGES,
  SINGAPORE_DISTRICTS,
} from '../api/communityApi';
import { updateSubscriptionPreferences } from '../api/communicationsApi';
import { syncPushToken, getNotificationPermission } from '../api/fcmService';
import '../styles/volunteer.css';

const VOL_NAV_ICONS = {
  home: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <path d="M9 21V12h6v9"/>
    </svg>
  ),
  programmes: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  ),
  jobs: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2M12 12v4M10 14h4"/>
    </svg>
  ),
  support: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  events: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  shifts: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  profile: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
};

const VOLUNTEER_TOOLS = [
  { key: 'home',    label: 'Home',               icon: VOL_NAV_ICONS.home    },
  { key: 'support', label: 'Support Management', icon: VOL_NAV_ICONS.support },
  { key: 'events',  label: 'Events Management',  icon: VOL_NAV_ICONS.events  },
  { key: 'profile', label: 'My Profile',         icon: VOL_NAV_ICONS.profile },
];

const SUPPORT_PILLS = ['View Available Roles', 'My Roles, Groups & Pairs', 'My Messages'];
const JOB_PILLS     = ['View Available Job Listings', 'My Job Listings & Applicants', 'Post A Job'];

const TOOL_PILLS = {
  support: [...SUPPORT_PILLS, ...JOB_PILLS],
  events:  ['View Upcoming Events', 'Volunteer for Event', 'Register for Event', 'Propose An Event'],
};

const PILL_TABS = {};

const TOOL_TABS = {
  home:    ['My Events', 'Opportunities', 'Office Hours'],
  profile: ['My Details', 'Availability', 'Subscription Preferences'],
};

function VolunteerPortal({ toolHint = 'programmes' }) {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { currentVolunteer, logout } = useVolunteerAuth();

  const resolvedTool = (toolHint === 'jobs' || toolHint === 'programmes') ? 'support' : toolHint;
  const resolvedPill = toolHint === 'jobs' ? 'View Available Job Listings' : (TOOL_PILLS[resolvedTool]?.[0] ?? null);

  const [activeTool, setActiveTool] = useState(resolvedTool);
  const [activePill, setActivePill] = useState(resolvedPill);
  const [activeTab,  setActiveTab]  = useState(
    resolvedPill ? (PILL_TABS[resolvedPill]?.[0] || '') : (TOOL_TABS[resolvedTool]?.[0] || '')
  );
  const [homeScrollSignal, setHomeScrollSignal] = useState(0);
  const [orgConfig, setOrgConfig] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db, 'organizations', orgId)).then(snap => {
      if (snap.exists()) setOrgConfig(snap.data());
    }).catch(() => {});
  }, [orgId]);

  useEffect(() => {
    const firstPill = TOOL_PILLS[activeTool]?.[0] ?? null;
    setActivePill(firstPill);
    if (firstPill) {
      setActiveTab(PILL_TABS[firstPill]?.[0] || '');
    } else {
      setActiveTab(TOOL_TABS[activeTool]?.[0] || '');
    }
  }, [activeTool]);

  useEffect(() => {
    if (activePill) setActiveTab(PILL_TABS[activePill]?.[0] || '');
  }, [activePill]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate(`/BespokeBridge/${orgId}/volunteer/login`);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const brandColors = orgConfig?.branding?.colors || {};
  const orgCssVars = {
    '--org-primary':    brandColors.primary    || '#3A7D44',
    '--org-secondary':  brandColors.secondary  || '#B8D9A0',
    '--org-accent':     brandColors.accent     || '#EBF5E4',
    '--org-background': brandColors.background || '#F5F0E8',
    '--org-text':       brandColors.text       || '#2D3A2E',
  };

  const enabledModules = orgConfig?.enabledModules;
  const supportMgmtEnabled = !enabledModules || !!enabledModules.supportmgmt;

  const visibleTools = VOLUNTEER_TOOLS.filter(t => {
    if (t.key === 'support') return supportMgmtEnabled;
    return true;
  });

  const volunteerName = currentVolunteer?.displayName
    || currentVolunteer?.name
    || (currentVolunteer?.firstName && currentVolunteer?.lastName
        ? `${currentVolunteer.firstName} ${currentVolunteer.lastName}`.trim()
        : null)
    || currentVolunteer?.firstName
    || currentVolunteer?.email
    || 'Volunteer';
  const volunteerId = currentVolunteer?.id || currentVolunteer?.uid || '';

  const renderTool = () => {
    const props = { orgId, volunteerId, volunteerName, activeTab };
    switch (activeTool) {
      case 'home':
        return (
          <VolunteerHome
            orgId={orgId}
            volunteerId={volunteerId}
            volunteerName={volunteerName}
            officeHours={orgConfig?.officeHours}
            orgName={orgConfig?.branding?.name || orgConfig?.aboutUs?.name || orgId}
            orgConfig={orgConfig}
            activeTab={activeTab}
            scrollSignal={homeScrollSignal}
          />
        );
      case 'support': {
        const pillProps = { orgId, volunteerId, volunteerName, activeTab: activePill };
        if (JOB_PILLS.includes(activePill)) return <JobBoardVolunteer {...pillProps} />;
        return <SupportNetworkVolunteer {...pillProps} />;
      }
      case 'events':
        return <MyEventsVolunteer orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} volunteerEmail={currentVolunteer?.email} activeTab={activePill} />;
      case 'profile':
        return <VolunteerProfile {...props} volunteer={currentVolunteer} />;
      default:
        return null;
    }
  };

  return (
    <div className="bb-vol-portal" style={orgCssVars}>
      {/* ── Header ── */}
      <header className="bb-vol-header">
        <div className="bb-vol-header-brand">
          <div className="bb-brand-row">
            <span className="bb-brand-name">BespokeBridge</span>
            <span className="bb-portal-type">Volunteer Portal</span>
            <span className="bb-org-id-badge">{orgId}</span>
          </div>
        </div>

        {/* ── User menu ── */}
        <div className="bb-vol-header-user" ref={userMenuRef} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NotificationBell orgId={orgId} recipientId={volunteerId} accentColor={brandColors.primary || '#3A7D44'} />
          <button
            className="bb-vol-user-menu"
            onClick={() => setUserMenuOpen(v => !v)}
          >
            <div className="bb-vol-user-avatar">
              {volunteerName.charAt(0).toUpperCase()}
            </div>
            <span className="bb-vol-user-name">{volunteerName}</span>
            <span style={{ fontSize: 10, opacity: 0.7 }}>▾</span>
          </button>
          {userMenuOpen && (
            <div className="bb-vol-user-dropdown">
              <div className="bb-vol-dropdown-info">
                <div className="bb-vol-dropdown-name">{volunteerName}</div>
                <div className="bb-vol-dropdown-email">{currentVolunteer?.email}</div>
              </div>
              <hr className="bb-vol-dropdown-divider" />
              <button
                className="bb-vol-dropdown-item"
                onClick={() => { setActiveTool('profile'); setUserMenuOpen(false); }}
              >
                👤 My Profile
              </button>
              <button className="bb-vol-dropdown-item bb-vol-dropdown-logout" onClick={handleLogout}>
                ⎋ Sign Out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* ── Sub-header: tools (row 1) + pills (row 2) + tabs (row 3) ── */}
      <div className="bb-tab-bar">
        {/* Row 1 — main tool icons */}
        <div className="bb-tab-bar-groups">
          {visibleTools.map(tool => (
            <button
              key={tool.key}
              className={`bb-tab-group-btn${activeTool === tool.key ? ' selected' : ''}`}
              onClick={() => setActiveTool(tool.key)}
            >
              <span className="bb-tab-group-icon">{tool.icon}</span>
              <span className="bb-tab-group-label">{tool.label}</span>
            </button>
          ))}
        </div>

        {/* Row 2 — pills (Programmes Management / Events Management) or simple tabs (Home / Profile) */}
        {TOOL_PILLS[activeTool] ? (
          <div className="bb-tab-bar-items">
            {TOOL_PILLS[activeTool].map(pill => (
              <button
                key={pill}
                className={`bb-tab-item-btn${activePill === pill ? ' selected' : ''}`}
                onClick={() => setActivePill(pill)}
              >
                {pill}
              </button>
            ))}
          </div>
        ) : TOOL_TABS[activeTool] ? (
          <div className="bb-tab-bar-items">
            {TOOL_TABS[activeTool].map(tab => (
              <button
                key={tab}
                className={`bb-tab-item-btn${activeTab === tab ? ' selected' : ''}`}
                onClick={() => {
                  setActiveTab(tab);
                  if (activeTool === 'home') setHomeScrollSignal(s => s + 1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>
        ) : null}

        {/* Row 3 — sub-tabs within a pill (e.g. Job Board → Post A Job / My Job Listings / My Job Applicants) */}
        {activePill && PILL_TABS[activePill]?.length > 0 && (
          <div className="bb-tab-bar-items bb-tab-bar-items--sub">
            {PILL_TABS[activePill].map(tab => (
              <button
                key={tab}
                className={`bb-tab-item-btn${activeTab === tab ? ' selected' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Main ── */}
      <main className="bb-vol-main">
        {renderTool()}
      </main>
    </div>
  );
}

/* ── Toast helper ── */
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
      background: type === 'error' ? '#DC2626' : '#059669',
      color: '#fff', padding: '10px 18px', borderRadius: 8,
      fontSize: 14, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    }}>
      {message}
    </div>
  );
}

/* ── Inline profile component ── */
function VolunteerProfile({ orgId, volunteerId, activeTab, volunteer: initialVolunteer }) {
  // ── My Details state ─────────────────────────────────────────────────────
  const [volData, setVolData]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState(null);

  const [phone, setPhone]       = useState('');
  const [address, setAddress]   = useState('');
  const [district, setDistrict] = useState('');
  const [bio, setBio]           = useState('');
  const [skills, setSkills]     = useState([]);
  const [languages, setLanguages] = useState([]);

  // ── Availability state ───────────────────────────────────────────────────
  const [availability, setAvailability] = useState('active');
  const [availSaving, setAvailSaving]   = useState(false);
  const [availSaved, setAvailSaved]     = useState(false);

  // ── Subscription preferences state ──────────────────────────────────────
  const [subEmail, setSubEmail]           = useState(true);
  const [subPush, setSubPush]             = useState(true);
  const [subWhatsapp, setSubWhatsapp]     = useState(true);
  const [subDirectMail, setSubDirectMail] = useState(false);
  const [subSaving, setSubSaving]         = useState(false);

  const showToast = (message, type = 'success') => setToast({ message, type });

  useEffect(() => {
    if (!orgId || !volunteerId) { setLoading(false); return; }
    const load = async () => {
      try {
        const v = await getVolunteer(orgId, volunteerId);
        if (v) {
          setVolData(v);
          setPhone(v.phone || '');
          setAddress(v.address || '');
          setDistrict(v.district || '');
          setBio(v.bio || '');
          setSkills(v.skills || []);
          setLanguages(v.languages || []);
          const sp = v.subscriptionPreferences || {};
          setSubEmail(sp.email !== undefined ? sp.email : true);
          setSubPush(sp.pushNotifications !== undefined ? sp.pushNotifications : true);
          setSubWhatsapp(sp.whatsApp !== undefined ? sp.whatsApp : (v.whatsappOptIn !== false));
          setSubDirectMail(sp.directMail !== undefined ? sp.directMail : false);
        }
      } catch (err) {
        console.error('Error loading volunteer profile:', err);
        showToast('Failed to load profile', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId, volunteerId]);

  const handleSkillToggle = (value) =>
    setSkills(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);

  const handleLanguageToggle = (value) =>
    setLanguages(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateVolunteer(orgId, volunteerId, { phone, address, district, bio, skills, languages });
      const updated = await getVolunteer(orgId, volunteerId);
      if (updated) setVolData(updated);
      setEditing(false);
      showToast('Details updated successfully');
    } catch (err) {
      console.error('Error saving volunteer profile:', err);
      showToast('Failed to save details', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (volData) {
      setPhone(volData.phone || '');
      setAddress(volData.address || '');
      setDistrict(volData.district || '');
      setBio(volData.bio || '');
      setSkills(volData.skills || []);
      setLanguages(volData.languages || []);
    }
    setEditing(false);
  };

  const fieldStyle = {
    display: 'flex', alignItems: 'center', padding: '10px 0',
    borderBottom: '1px solid #F3F4F6', gap: 16,
  };
  const labelStyle = { width: 130, flexShrink: 0, fontSize: 13, color: '#6B7280', fontWeight: 500 };
  const valueStyle = { flex: 1, fontSize: 13, color: '#111827' };
  const inputStyle = { flex: 1, padding: '6px 10px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 };

  // ── My Details tab ───────────────────────────────────────────────────────
  if (activeTab === 'My Details') {
    const display = volData || initialVolunteer;
    return (
      <div className="bb-vol-tool">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="bb-vol-tool-header">
          <h2>My Profile</h2>
        </div>
        <div className="bb-vol-section">
          {loading ? (
            <p style={{ color: '#6B7280', fontSize: 14 }}>Loading…</p>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Personal Information</h3>
                {!editing && (
                  <button
                    className="bb-btn-outline-sm"
                    onClick={() => setEditing(true)}
                    style={{ fontSize: 13 }}
                  >
                    Edit
                  </button>
                )}
              </div>

              {/* Name — always read-only */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Name</span>
                <span style={{ ...valueStyle, color: editing ? '#9CA3AF' : '#111827' }}>
                  {display?.displayName || display?.name || '—'}
                  {editing && <span style={{ fontSize: 11, marginLeft: 6, color: '#9CA3AF' }}>(cannot change)</span>}
                </span>
              </div>

              {/* Email — always read-only */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Email</span>
                <span style={{ ...valueStyle, color: editing ? '#9CA3AF' : '#111827' }}>
                  {display?.email || '—'}
                  {editing && <span style={{ fontSize: 11, marginLeft: 6, color: '#9CA3AF' }}>(cannot change)</span>}
                </span>
              </div>

              {/* Phone */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Phone</span>
                {editing ? (
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="e.g. +65 9123 4567"
                    style={inputStyle}
                  />
                ) : (
                  <span style={valueStyle}>{volData?.phone || '—'}</span>
                )}
              </div>

              {/* Address */}
              <div style={fieldStyle}>
                <span style={labelStyle}>Address</span>
                {editing ? (
                  <input
                    type="text"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="e.g. 20 Joo Chiat Place, #03-05, Singapore 237466"
                    style={inputStyle}
                  />
                ) : (
                  <span style={valueStyle}>{volData?.address || '—'}</span>
                )}
              </div>

              {/* District */}
              <div style={fieldStyle}>
                <span style={labelStyle}>District</span>
                {editing ? (
                  <select
                    value={district}
                    onChange={e => setDistrict(e.target.value)}
                    style={inputStyle}
                  >
                    <option value="">Select district…</option>
                    {SINGAPORE_DISTRICTS.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                ) : (
                  <span style={valueStyle}>{volData?.district || '—'}</span>
                )}
              </div>

              {/* Skills */}
              <div style={{ ...fieldStyle, alignItems: 'flex-start', paddingTop: 12 }}>
                <span style={{ ...labelStyle, paddingTop: 2 }}>Skills</span>
                {editing ? (
                  <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {VOLUNTEER_SKILLS.map(s => (
                      <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={skills.includes(s.value)}
                          onChange={() => handleSkillToggle(s.value)}
                        />
                        {s.label}
                      </label>
                    ))}
                  </div>
                ) : (
                  <span style={valueStyle}>
                    {(volData?.skills || []).length === 0
                      ? <span style={{ color: '#9CA3AF' }}>None added yet</span>
                      : (volData?.skills || []).map(v => {
                          const label = VOLUNTEER_SKILLS.find(s => s.value === v)?.label || v;
                          return (
                            <span key={v} style={{ display: 'inline-block', background: '#EEF2FF', color: '#4338CA', borderRadius: 12, padding: '2px 10px', fontSize: 12, marginRight: 6, marginBottom: 4 }}>
                              {label}
                            </span>
                          );
                        })
                    }
                  </span>
                )}
              </div>

              {/* Languages */}
              <div style={{ ...fieldStyle, alignItems: 'flex-start', paddingTop: 12 }}>
                <span style={{ ...labelStyle, paddingTop: 2 }}>Languages</span>
                {editing ? (
                  <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {LANGUAGES.map(l => (
                      <label key={l.value} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={languages.includes(l.value)}
                          onChange={() => handleLanguageToggle(l.value)}
                        />
                        {l.label}
                      </label>
                    ))}
                  </div>
                ) : (
                  <span style={valueStyle}>
                    {(volData?.languages || []).length === 0
                      ? <span style={{ color: '#9CA3AF' }}>None added yet</span>
                      : (volData?.languages || []).map(v => {
                          const label = LANGUAGES.find(l => l.value === v)?.label || v;
                          return (
                            <span key={v} style={{ display: 'inline-block', background: '#F0FDF4', color: '#16A34A', borderRadius: 12, padding: '2px 10px', fontSize: 12, marginRight: 6, marginBottom: 4 }}>
                              {label}
                            </span>
                          );
                        })
                    }
                  </span>
                )}
              </div>

              {/* Bio */}
              <div style={{ ...fieldStyle, alignItems: 'flex-start', paddingTop: 12, borderBottom: 'none' }}>
                <span style={{ ...labelStyle, paddingTop: 2 }}>Bio</span>
                {editing ? (
                  <textarea
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    placeholder="Tell us a bit about yourself…"
                    rows={3}
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                ) : (
                  <span style={valueStyle}>{volData?.bio || <span style={{ color: '#9CA3AF' }}>Not provided</span>}</span>
                )}
              </div>

              {/* Save / Cancel */}
              {editing && (
                <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                  <button
                    className="bb-btn-primary"
                    style={{ background: '#4338CA' }}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  <button
                    className="bb-btn-outline-sm"
                    onClick={handleCancel}
                    disabled={saving}
                    style={{ fontSize: 13 }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  const handleSaveSubscriptions = async () => {
    if (!subEmail && !subPush && !subWhatsapp && !subDirectMail) {
      showToast('At least one communication channel must be selected.', 'error');
      return;
    }
    setSubSaving(true);
    try {
      const prefs = { email: subEmail, push: subPush, whatsapp: subWhatsapp, directMail: subDirectMail };
      await updateSubscriptionPreferences('volunteers', volunteerId, prefs);
      if (subPush) {
        // Runs inside the Save click, so the browser can show its "Allow notifications?" dialog.
        const result = await syncPushToken('volunteer', { prompt: true });
        if (result === 'saved') showToast('Preferences saved. Push notifications are on for this device.');
        else if (getNotificationPermission() === 'denied') showToast('Preferences saved, but notifications are blocked in this browser. Allow them in the browser\'s site settings.', 'error');
        else showToast('Preferences saved, but push could not be set up on this device.', 'error');
      } else {
        showToast('Subscription preferences saved');
      }
    } catch (err) {
      console.error('Error saving subscription preferences:', err);
      showToast('Failed to save preferences', 'error');
    } finally {
      setSubSaving(false);
    }
  };

  // ── Availability tab ─────────────────────────────────────────────────────
  if (activeTab === 'Availability') {
    return (
      <div className="bb-vol-tool">
        <div className="bb-vol-tool-header">
          <h2>My Profile</h2>
        </div>
        <div className="bb-vol-section">
          <p style={{ color: '#6B7280', marginBottom: 16 }}>
            Set your general availability for volunteer activities. This affects your visibility in the Support Network matching system.
          </p>
          <div className="bb-form-group">
            <label>Availability Status</label>
            <select className="bb-select" value={availability} onChange={e => setAvailability(e.target.value)}>
              <option value="active">Active — available for matching</option>
              <option value="unavailable">Unavailable — paused</option>
            </select>
          </div>
          {availSaved && <div className="bb-alert bb-alert--info">Availability updated.</div>}
          <button
            className="bb-btn-primary"
            style={{ background: '#4338CA', marginTop: 8 }}
            disabled={availSaving}
            onClick={() => { setAvailSaving(true); setTimeout(() => { setAvailSaving(false); setAvailSaved(true); }, 600); }}
          >
            {availSaving ? 'Saving…' : 'Save Availability'}
          </button>
        </div>
      </div>
    );
  }

  // ── Subscription Preferences tab ────────────────────────────────────────
  if (activeTab === 'Subscription Preferences') {
    const noneSelected = !subEmail && !subPush && !subWhatsapp && !subDirectMail;
    const rowStyle = {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 0', borderBottom: '1px solid #F3F4F6',
    };
    const CHANNELS = [
      { key: 'email',       label: 'Email',               desc: 'Receive newsletters, updates and event info via email',         val: subEmail,      set: setSubEmail },
      { key: 'push',        label: 'Push Notifications',  desc: 'Receive instant alerts on your device for events and shifts',   val: subPush,       set: setSubPush },
      { key: 'whatsapp',    label: 'WhatsApp',             desc: 'Receive messages and reminders via WhatsApp',                   val: subWhatsapp,   set: setSubWhatsapp },
      { key: 'directMail',  label: 'Direct Mail',          desc: 'Receive physical letters and printed materials at your address', val: subDirectMail, set: setSubDirectMail },
    ];
    return (
      <div className="bb-vol-tool">
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
        <div className="bb-vol-tool-header">
          <h2>My Profile</h2>
        </div>
        <div className="bb-vol-section">
          <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 16 }}>
            Choose how you'd like to receive communications. At least one channel is required.
          </p>
          <div style={{ padding: '10px 14px', background: '#FFFBEB', border: '1px solid #FCD34D', borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
            <strong>At least one channel is required</strong> so we can reach you with important updates.
          </div>
          {CHANNELS.map((ch, i) => (
            <div key={ch.key} style={{ ...rowStyle, ...(i === CHANNELS.length - 1 ? { borderBottom: 'none' } : {}) }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>{ch.label}</div>
                <div style={{ fontSize: 12, color: '#6B7280', marginTop: 2 }}>{ch.desc}</div>
              </div>
              <label className="bb-member-toggle-switch">
                <input type="checkbox" checked={ch.val} onChange={e => ch.set(e.target.checked)} />
                <span className="bb-member-toggle-slider"></span>
              </label>
            </div>
          ))}
          {noneSelected && (
            <div style={{ marginTop: 12, padding: '8px 12px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6, fontSize: 13, color: '#B91C1C' }}>
              Please select at least one channel before saving.
            </div>
          )}
          <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
            <button
              className="bb-btn-primary"
              style={{ background: '#4338CA' }}
              onClick={handleSaveSubscriptions}
              disabled={subSaving || noneSelected}
            >
              {subSaving ? 'Saving…' : 'Save Preferences'}
            </button>
          </div>
          <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 12 }}>
            Changes take effect immediately. Opting out of a channel means you will no longer receive
            that type of communication from BespokeBridge.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default VolunteerPortal;
